import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { CONFIG } from '../config/config';
import { updateProfileSoft } from '../api/users';

const UserContext = createContext();
export function useUser() {
  return useContext(UserContext);
}

function getStoredToken() {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
}
function clearStoredAuth() {
  [localStorage, sessionStorage].forEach((s) => {
    s.removeItem('token');
    s.removeItem('user');
    s.removeItem('role');
    s.removeItem('profile');
    s.removeItem('etablissement');
    s.removeItem('nbQCMAnswered');
    s.removeItem('nbCorrectQCMAnswered');
  });
}

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [etablissement, setEtablissement] = useState(
    JSON.parse(localStorage.getItem('etablissement')) ??
      JSON.parse(sessionStorage.getItem('etablissement')),
  );
  const [profile, setProfile] = useState(
    JSON.parse(localStorage.getItem('profile')) ?? JSON.parse(sessionStorage.getItem('profile')),
  );
  const [chosenGroup, setChosenGroup] = useState(
    JSON.parse(localStorage.getItem('chosenGroup')) ??
      JSON.parse(sessionStorage.getItem('chosenGroup')),
  );
  const [nbQCMAnswered, setNbQCMAnswered] = useState(
    JSON.parse(localStorage.getItem('nbQCMAnswered')) ??
      JSON.parse(sessionStorage.getItem('nbQCMAnswered')),
  );
  const [nbCorrectQCMAnswered, setNbCorrectQCMAnswered] = useState(
    JSON.parse(localStorage.getItem('nbCorrectQCMAnswered')) ??
      JSON.parse(sessionStorage.getItem('nbCorrectQCMAnswered')),
  );

  function handleSetProfile(profile) {
    const storage = localStorage.getItem('profile') ? localStorage : sessionStorage;
    storage.setItem('profile', JSON.stringify(profile));
    setProfile(profile);
    setEtablissement(profile.initEtab);
    setNbQCMAnswered(0);
    setNbCorrectQCMAnswered(0);
  }

  function handleSetNbQCMAnswered(nbQCMAnswered) {
    const storage = localStorage.getItem('nbQCMAnswered') ? localStorage : sessionStorage;
    storage.setItem('nbQCMAnswered', nbQCMAnswered);
    setNbQCMAnswered(nbQCMAnswered);
  }

  function handleSetNbCorrectQCMAnswered(nbCorrectQCMAnswered) {
    const storage = localStorage.getItem('nbCorrectQCMAnswered') ? localStorage : sessionStorage;
    storage.setItem('nbCorrectQCMAnswered', nbCorrectQCMAnswered);
    setNbCorrectQCMAnswered(nbCorrectQCMAnswered);
  }

  function handleSetEtablissement(etablissement) {
    const storage = localStorage.getItem('etablissement') ? localStorage : sessionStorage;
    storage.setItem('etablissement', JSON.stringify(etablissement));
    if (profile) {
      updateProfileSoft(profile.profile_id, {
        default_etablissement_id: etablissement.etablissement_id,
      });
    }
    setEtablissement(etablissement);
  }

  function handleSetChosenGroup(grp) {
    const storage = localStorage.getItem('chosenGroup') ? localStorage : sessionStorage;
    storage.setItem('chosenGroup', JSON.stringify(grp));
    setChosenGroup(grp);
  }

  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      setLoading(false);
      return;
    }

    fetch(`${CONFIG.API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((u) => {
        setUser(u);
        const storage = localStorage.getItem('token') ? localStorage : sessionStorage;
        storage.setItem('user', JSON.stringify(u));
        storage.setItem('role', u.role || 'infirmiere');
      })
      .catch(() => {
        /* silencieux */
      })
      .finally(() => setLoading(false));
  }, []);

  const logout = useMemo(
    () => () => {
      clearStoredAuth();
      setUser(null);
      if (window.location.pathname !== '/login') window.location.replace('/login');
    },
    [],
  );

  const value = useMemo(
    () => ({
      user,
      setUser,
      loading,
      logout,
      profile,
      handleSetProfile,
      etablissement,
      handleSetEtablissement,
      chosenGroup,
      handleSetChosenGroup,
      nbQCMAnswered,
      handleSetNbQCMAnswered,
      nbCorrectQCMAnswered,
      handleSetNbCorrectQCMAnswered,
    }),
    [
      user,
      loading,
      logout,
      etablissement,
      profile,
      chosenGroup,
      nbQCMAnswered,
      nbCorrectQCMAnswered,
    ],
  );
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
